import { Refresh as RefreshIcon } from "@mui/icons-material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { subDays } from "date-fns";
import moment from "moment";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPayments,
  type Payment,
  type PaymentListResponse,
} from "../../apis/customerProfile/apis";
import { TAG_COLOR } from "../../constants/const";
import { strings } from "../../I18n";
import {
  convertDateFromUtc0ToLocal,
  modifyCurrencyToSymbolAndValue,
} from "../../utils/utils";
import Tag from "../Tags";
import CompactDateRangePicker from "../wallets/CompactDateRangePicker";
import ReceiptDrawer from "./ReceiptDrawer";

interface Props {
  userId: number;
  walletData?: any;
  legacyTransactions?: any[];
  currency?: string;
}

type TransactionSource = "unified" | "legacy" | "payment";

interface MergedTransaction {
  id: string;
  source: TransactionSource;
  dateTime: number;
  displayDate: string;
  type?: string;
  amount?: number;
  currency?: string;
  status?: string;
  note?: string;
  reason?: any;
  particulars?: string;
  debit?: number;
  credit?: number;
  paymentKey?: string;
  paymentType?: string;
}

const paymentStatusColors: Record<
  string,
  "success" | "warning" | "error" | "info" | "default"
> = {
  CAPTURED: "success",
  AUTHORIZED: "warning",
  CANCELED: "default",
  ERROR: "error",
};

const paymentTypeColors: Record<
  string,
  "primary" | "secondary" | "info" | "success" | "warning" | "default"
> = {
  PAYMENT: "primary",
  TOP_UP: "success",
  ADJUST_WALLET: "secondary",
  WALLET_PAYMENT: "info",
  RIDE_PASS: "warning",
  CUSTOM: "default",
};

const formatPaymentType = (type: string): string => {
  return type?.replace(/_/g, " ") || type;
};

const CustomerTransactions: React.FC<Props> = ({
  userId,
  walletData,
  legacyTransactions = [],
  currency: currencyProp,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentListResponse | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 30),
    endDate: new Date(),
    key: "selection",
  });
  const [selectedPaymentKey, setSelectedPaymentKey] = useState<string | null>(
    null
  );

  const currency = walletData?.unifiedWallet?.currency || currencyProp;

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPayments({
        userId,
        page: 0,
        size: 1000,
        startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
        endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
      });
      setPayments(data);
    } catch (e) {
      setError(strings("descriptions.noPaymentsFound"));
    } finally {
      setLoading(false);
    }
  }, [userId, dateRange]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const getReasonDetails = (
    reason: any
  ): { text: string; tag: string; tagColor: string } => {
    if (!reason) return { text: "-", tag: "", tagColor: "" };

    if (typeof reason === "string")
      return { text: reason, tag: "", tagColor: "" };

    if (typeof reason === "object") {
      switch (reason.type) {
        case "ADMIN_INTERVENTION":
          return {
            text: reason.reason || "Admin adjustment",
            tag: "Admin",
            tagColor: TAG_COLOR.MAGENTA,
          };
        case "PAYMENT_CONFIGURATION_TRANSACTION":
          return {
            text: `Top up (ID: ${
              reason.payment_configuration_transaction_id?.slice(0, 8) || "N/A"
            }...)`,
            tag: "Top up",
            tagColor: TAG_COLOR.GREEN,
          };
        case "TRIP":
          return {
            text: `Trip payment (ID: ${reason.trip_id?.slice(0, 8) || "N/A"}...)`,
            tag: "Trip",
            tagColor: TAG_COLOR.RED,
          };
        default:
          if (reason.type) {
            return {
              text: reason.type
                .replace(/_/g, " ")
                .toLowerCase()
                .replace(/\b\w/g, (l: string) => l.toUpperCase()),
              tag: "",
              tagColor: "",
            };
          }
      }
    }

    return { text: JSON.stringify(reason), tag: "", tagColor: "" };
  };

  const mergedTransactions = useMemo(() => {
    const unifiedTransactions = walletData?.unifiedTransactions || [];
    const legacyArray = (legacyTransactions as any)?.content || [];

    const unified: MergedTransaction[] = unifiedTransactions
      .filter((txn: any) => {
        if (txn.reason?.type === "ADMIN_INTERVENTION" && txn.reason?.reason) {
          return !txn.reason.reason.includes("Legacy wallet migration");
        }
        return true;
      })
      .map((txn: any) => ({
        id: `unified-${txn.id}`,
        source: "unified" as const,
        dateTime: new Date(txn.creationDatetime).getTime(),
        displayDate: moment(txn.creationDatetime).format("DD.MM.YYYY HH:mm"),
        type: txn.type,
        amount: txn.amountMinor,
        currency: txn.currency || currency,
        note: txn.note,
        reason: txn.reason,
      }));

    const legacy: MergedTransaction[] = legacyArray
      .filter((txn: any) => {
        if (txn.particulars) {
          return !txn.particulars.includes(
            "Zeroed out after migration to Lisa unified wallet"
          );
        }
        return true;
      })
      .map((txn: any) => ({
        id: `legacy-${txn.id}`,
        source: "legacy" as const,
        dateTime: new Date(
          convertDateFromUtc0ToLocal(txn.dateCreated)
        ).getTime(),
        displayDate: moment(convertDateFromUtc0ToLocal(txn.dateCreated)).format(
          "DD.MM.YYYY HH:mm"
        ),
        note: txn.note,
        particulars: txn.particulars,
        debit: txn.debit,
        credit: txn.credit,
        paymentType: txn.paymentType,
      }));

    const paymentsList: MergedTransaction[] = (payments?.content || []).map(
      (payment: Payment) => ({
        id: `payment-${payment.id}`,
        source: "payment" as const,
        dateTime: new Date(payment.dateCreated).getTime(),
        displayDate: moment(payment.dateCreated).format("DD.MM.YYYY HH:mm"),
        type: payment.type,
        amount: payment.amount,
        currency: payment.currency,
        status: payment.status,
        paymentKey: payment.key,
      })
    );

    const all = [...unified, ...legacy, ...paymentsList];
    all.sort((a, b) => b.dateTime - a.dateTime);

    const startTime = new Date(dateRange.startDate).setHours(0, 0, 0, 0);
    const endTime = new Date(dateRange.endDate).setHours(23, 59, 59, 999);

    return all.filter(
      (txn) => txn.dateTime >= startTime && txn.dateTime <= endTime
    );
  }, [walletData, legacyTransactions, payments, dateRange, currency]);

  const handleDateRangeChange = (range: {
    startDate: Date;
    endDate: Date;
    key: string;
  }) => {
    setDateRange(range);
  };

  const handleOpenReceipt = (paymentKey: string) => {
    setSelectedPaymentKey(paymentKey);
  };

  const handleCloseReceipt = () => {
    setSelectedPaymentKey(null);
  };

  const renderTransactionRow = (txn: MergedTransaction) => {
    if (txn.source === "unified") {
      const reasonDetails = getReasonDetails(txn.reason);
      return (
        <TableRow key={txn.id} hover={true}>
          <TableCell>{txn.displayDate}</TableCell>
          <TableCell>
            <Chip label="Wallet" size="small" color="info" variant="outlined" />
          </TableCell>
          <TableCell>
            {reasonDetails.tag ? (
              <Tag text={reasonDetails.tag} colorTag={reasonDetails.tagColor} />
            ) : (
              "-"
            )}
          </TableCell>
          <TableCell>{txn.note || "-"}</TableCell>
          <TableCell>
            <Typography
              sx={{
                color: txn.type === "ADD" ? "#389e0d" : "#cf1322",
                fontWeight: 500,
              }}
            >
              {txn.type === "ADD" ? "+" : "-"}
              {modifyCurrencyToSymbolAndValue(
                txn.currency || currency,
                Math.abs(txn.amount || 0) / 100
              )}
            </Typography>
          </TableCell>
          <TableCell>-</TableCell>
          <TableCell>-</TableCell>
        </TableRow>
      );
    }

    if (txn.source === "legacy") {
      const isAdminAdjustment = txn.paymentType === "ADJUST_WALLET";
      return (
        <TableRow key={txn.id} hover={true}>
          <TableCell>{txn.displayDate}</TableCell>
          <TableCell>
            <Chip label="Wallet" size="small" color="info" variant="outlined" />
          </TableCell>
          <TableCell>
            {isAdminAdjustment ? (
              <Tag text="Admin" colorTag={TAG_COLOR.MAGENTA} />
            ) : (
              txn.particulars || "-"
            )}
          </TableCell>
          <TableCell>{txn.note || "-"}</TableCell>
          <TableCell>
            {txn.debit && txn.debit !== 0 ? (
              <Typography sx={{ color: "#cf1322", fontWeight: 500 }}>
                -{modifyCurrencyToSymbolAndValue(currency, txn.debit / 100)}
              </Typography>
            ) : txn.credit && txn.credit !== 0 ? (
              <Typography sx={{ color: "#389e0d", fontWeight: 500 }}>
                +{modifyCurrencyToSymbolAndValue(currency, txn.credit / 100)}
              </Typography>
            ) : (
              "-"
            )}
          </TableCell>
          <TableCell>-</TableCell>
          <TableCell>-</TableCell>
        </TableRow>
      );
    }

    if (txn.source === "payment") {
      return (
        <TableRow key={txn.id} hover={true}>
          <TableCell>{txn.displayDate}</TableCell>
          <TableCell>
            <Chip
              label={formatPaymentType(txn.type || "")}
              size="small"
              color={paymentTypeColors[txn.type || ""] || "default"}
            />
          </TableCell>
          <TableCell>-</TableCell>
          <TableCell>-</TableCell>
          <TableCell>
            {modifyCurrencyToSymbolAndValue(
              txn.currency || currency,
              (txn.amount || 0) / 100
            )}
          </TableCell>
          <TableCell>
            <Chip
              label={txn.status}
              size="small"
              color={paymentStatusColors[txn.status || ""] || "default"}
            />
          </TableCell>
          <TableCell>
            {txn.paymentKey && txn.status === "CAPTURED" ? (
              <Tooltip title={strings("descriptions.viewReceipt")}>
                <IconButton
                  size="small"
                  onClick={() => handleOpenReceipt(txn.paymentKey!)}
                >
                  <ReceiptIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              "-"
            )}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  };

  return (
    <>
      <Card elevation={3}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6">
              {strings("descriptions.transactions")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <CompactDateRangePicker
                dateRange={dateRange}
                onChange={handleDateRangeChange}
              />
              <Tooltip title={strings("buttons.refresh")}>
                <IconButton onClick={fetchPayments} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !mergedTransactions.length ? (
            <Alert severity="info">
              {strings("descriptions.noTransactionsFound")}
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>{strings("descriptions.dateAndTime")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.type")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.particulars")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.note")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.amount")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.status")}</strong>
                    </TableCell>
                    <TableCell>
                      <strong>{strings("descriptions.receipt")}</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mergedTransactions.map(renderTransactionRow)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ReceiptDrawer
        open={!!selectedPaymentKey}
        paymentKey={selectedPaymentKey}
        onClose={handleCloseReceipt}
      />
    </>
  );
};

export default CustomerTransactions;
